import React from "react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

/**
 * Reusable form modal with:
 *   • Dark gradient header + close button
 *   • React Hook Form context (use RHFTextField / RHFSelect inside)
 *   • Zod schema validation (optional — pass schema prop)
 *   • Loading state on the submit button
 *   • Auto-reset when the modal closes or defaultValues change
 *
 * Props:
 *   open          boolean               — controls visibility
 *   onClose       () => void            — called after reset
 *   title         string                — header title
 *   schema        ZodSchema (optional)  — if provided, enables Zod validation
 *   defaultValues object                — initial / reset values; change this
 *                                         per-record for edit modals
 *   onSubmit      async (data) => void  — called with validated data;
 *                                         throw to keep modal open on error
 *   submitLabel   string (default "Save")
 *   width         number (default 520)  — sm breakpoint width in px
 *   children      ReactNode | (methods) => ReactNode
 *                   — form body (Grid items etc.)
 *                   — receives react-hook-form methods if function form used
 *
 * Example:
 *   import { z } from "zod";
 *   import FormModal from "../components/form/FormModal";
 *   import RHFTextField from "../components/form/RHFTextField";
 *   import RHFSelect from "../components/form/RHFSelect";
 *
 *   const schema = z.object({
 *     location: z.string().min(1, "Location is required"),
 *     vehicle:  z.string().min(1, "Vehicle is required"),
 *     remarks:  z.string().optional(),
 *   });
 *
 *   <FormModal
 *     open={open}
 *     onClose={handleClose}
 *     title="Edit Customer Support"
 *     schema={schema}
 *     defaultValues={{ location: record.location, vehicle: String(record.vehicle_id), remarks: record.remarks }}
 *     onSubmit={async (data) => {
 *       await updateCustomerSupportNewInstallation(record.id, data);
 *       showSnackbar("Saved", "success");
 *     }}
 *     submitLabel="Update"
 *   >
 *     {({ control }) => (
 *       <Grid container spacing={2.5}>
 *         <Grid item xs={12} md={6}>
 *           <RHFSelect control={control} name="vehicle" label="Vehicle" options={trucks} required />
 *         </Grid>
 *         <Grid item xs={12} md={6}>
 *           <RHFTextField control={control} name="location" label="Location" required />
 *         </Grid>
 *         <Grid item xs={12}>
 *           <RHFTextField control={control} name="remarks" label="Remarks" multiline rows={3} />
 *         </Grid>
 *       </Grid>
 *     )}
 *   </FormModal>
 */
const FormModal = ({
  open,
  onClose,
  title,
  schema,
  defaultValues = {},
  onSubmit,
  submitLabel = "Save",
  children,
  width = 520,
}) => {
  const methods = useForm({
    resolver: schema ? zodResolver(schema) : undefined,
    defaultValues,
  });

  const { handleSubmit, reset, formState: { isSubmitting, errors } } = methods;

  // Sync form values when the modal opens with new defaultValues (e.g. edit different record)
  React.useEffect(() => {
    if (open) reset(defaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    reset(defaultValues);
    onClose();
  };

  const onValid = async (data) => {
    try {
      await onSubmit(data);
      reset(defaultValues);
      onClose();
    } catch {
      // Parent's onSubmit should call showSnackbar; we just keep the modal open
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "92vw", sm: width },
          bgcolor: "background.paper",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          borderRadius: 3,
          maxHeight: "90vh",
          overflowY: "auto",
          outline: "none",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #0b1e3d 0%, #0d2b5e 100%)",
            px: 3, py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderRadius: "12px 12px 0 0",
          }}
        >
          <Typography variant="h6" sx={{ color: "#fff", fontWeight: 600, fontSize: "1rem" }}>
            {title}
          </Typography>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: "rgba(255,255,255,0.7)",
              "&:hover": { color: "#fff", background: "rgba(255,255,255,0.1)" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Form body */}
        <FormProvider {...methods}>
          <Box
            component="form"
            onSubmit={handleSubmit(onValid)}
            sx={{ p: 3 }}
            noValidate
          >
            {/* Root-level error (e.g. network) */}
            {errors.root && (
              <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                {errors.root.message}
              </Typography>
            )}

            {typeof children === "function" ? children(methods) : children}

            {/* Footer */}
            <Box
              sx={{
                mt: 3, pt: 2,
                borderTop: "1px solid",
                borderColor: "divider",
                display: "flex",
                justifyContent: "flex-end",
                gap: 1.5,
              }}
            >
              <Button variant="outlined" onClick={handleClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                variant="contained"
                type="submit"
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {isSubmitting ? "Saving…" : submitLabel}
              </Button>
            </Box>
          </Box>
        </FormProvider>
      </Box>
    </Modal>
  );
};

export default FormModal;
